/**
 * HTML Sanitization Utility
 *
 * Provides functions to escape HTML content and prevent XSS attacks.
 * Use these functions when inserting user-controlled or server-provided
 * data into the DOM via innerHTML or similar methods.
 */

/**
 * Escapes HTML special characters to prevent XSS attacks
 *
 * @param {string|null|undefined} text - The text to escape
 * @returns {string} The escaped text safe for HTML insertion
 *
 * @example
 * const userInput = '<script>alert("XSS")</script>'
 * element.innerHTML = `<div>${escapeHtml(userInput)}</div>`
 * // Results in: <div>&lt;script&gt;alert("XSS")&lt;/script&gt;</div>
 */
export function escapeHtml(text) {
  if (text === null || text === undefined || text === '') return ''

  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

/**
 * Escapes HTML attribute values to prevent XSS in attributes
 *
 * @param {string|null|undefined} value - The attribute value to escape
 * @returns {string} The escaped attribute value
 *
 * @example
 * const url = 'javascript:alert("XSS")'
 * element.innerHTML = `<a href="${escapeAttr(url)}">Link</a>`
 */
export function escapeAttr(value) {
  if (!value) return ''

  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

/**
 * Strips HTML tags from a string, returning plain text content.
 * Uses DOMParser for safe parsing - the HTML is never executed.
 *
 * @param {string|null|undefined} html - The HTML string to strip
 * @returns {string} Plain text content without HTML tags
 *
 * @example
 * const html = '<strong>Hello</strong> <em>World</em>'
 * stripHtml(html) // Returns: 'Hello World'
 *
 * @example
 * const dangerous = '<script>alert("XSS")</script>Visible text'
 * stripHtml(dangerous) // Returns: 'Visible text'
 */
export function stripHtml(html) {
  if (!html) return ''
  const doc = new DOMParser().parseFromString(html, 'text/html')
  return doc.body.textContent || ''
}

/**
 * Sanitizes HTML by allowlisting safe tags and attributes.
 * Disallowed tags are removed but their text content is preserved.
 * Uses DOMParser for safe parsing - the HTML is never executed.
 *
 * @param {string|null|undefined} html - The HTML string to sanitize
 * @param {Set<string>} [allowedTags] - Uppercase tag names to allow (defaults to banner-safe tags)
 * @param {Set<string>} [allowedAttrs] - Lowercase attribute names to allow
 * @returns {string} Sanitized HTML with only allowed tags and attributes
 *
 * @example
 * sanitizeHtml('<b>Bold</b><script>alert("XSS")</script>')
 * // Returns: '<b>Bold</b>'
 */
const DEFAULT_ALLOWED_TAGS = new Set([
  'P', 'BR', 'DIV', 'SPAN',
  'STRONG', 'B', 'EM', 'I', 'U', 'SMALL',
  'A',
  'UL', 'OL', 'LI',
  'HR'
])

const DEFAULT_ALLOWED_ATTRS = new Set([
  'href', 'title', 'rel', 'class', 'target'
])

export function sanitizeHtml(html, allowedTags = DEFAULT_ALLOWED_TAGS, allowedAttrs = DEFAULT_ALLOWED_ATTRS) {
  if (!html) return ''
  const doc = new DOMParser().parseFromString(html, 'text/html')

  const clean = (node) => {
    for (const child of [...node.childNodes]) {
      if (child.nodeType === 1) {
        if (!allowedTags.has(child.tagName)) {
          child.replaceWith(...child.childNodes)
          clean(node)
          return
        }
        for (const attr of [...child.attributes]) {
          if (!allowedAttrs.has(attr.name.toLowerCase())) {
            child.removeAttribute(attr.name)
          } else if (attr.name.toLowerCase() === 'href') {
            const hrefVal = attr.value.replace(/\s/g, '').toLowerCase()
            if (hrefVal.startsWith('javascript:') || hrefVal.startsWith('data:') || hrefVal.startsWith('vbscript:')) {
              child.removeAttribute(attr.name)
            }
          }
        }
        clean(child)
      }
    }
  }

  clean(doc.body)
  return doc.body.innerHTML
}
